/**
 * The full incident history, and one incident on its own URL.
 *
 * Grouped by month and paged with a keyset cursor, so a page that has been
 * running for three years still loads in one query. Every notice email links to
 * the permalink below, which is why it works on a cold URL with no session and
 * no prior request.
 */

import { useEffect, useState } from 'react';
import { ds } from '../ds';
import { api, errorText, type IncidentWire } from '../api';
import { Eyebrow, Footnote, Page, PageTitle, Panel } from '../components/Chrome';
import { IncidentCard } from '../components/IncidentCard';
import { Link, useRouter } from '../router';
import { display, monoPlain } from '../theme';

import { useT } from '../i18n';
/** "August 2026" from a unix timestamp — the grouping key and its own heading. */
const monthOf = (unixSeconds: number): string =>
  new Date(unixSeconds * 1000).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

export function Incidents() {
  const t = useT();
  const { Button } = ds();
  const [incidents, setIncidents] = useState<IncidentWire[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load(before?: number) {
    setLoading(true);
    try {
      const page = await api.incidents(before);
      setIncidents((prev) => (before ? [...prev, ...page.incidents] : page.incidents));
      setCursor(page.nextCursor);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load the incident history.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Loading the first page once on mount; `load` is stable enough for that
    // and re-running on every render would page forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Grouped in render rather than stored grouped, so appending a page does not
  // have to merge into an existing month.
  const months: { month: string; items: IncidentWire[] }[] = [];
  for (const incident of incidents) {
    const month = monthOf(incident.startedAt);
    const last = months[months.length - 1];
    if (last?.month === month) last.items.push(incident);
    else months.push({ month, items: [incident] });
  }

  return (
    <Page>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <PageTitle title={t('incidents.title')} meta={t('incidents.meta')} />
        <Link to="/" style={{ ...monoPlain, textDecoration: 'none' }}>
          {t('incidents.back_status')}
        </Link>
      </div>

      {error && (
        <Panel>
          <Footnote>{error}</Footnote>
        </Panel>
      )}

      {!loading && !incidents.length && !error && (
        <Panel>
          <Footnote>{t('incidents.empty')}</Footnote>
        </Panel>
      )}

      {months.map((group) => (
        <Panel key={group.month} style={{ display: 'grid', gap: 20 }}>
          <Eyebrow>{group.month}</Eyebrow>
          {group.items.map((incident) => (
            <IncidentCard key={incident.id} incident={incident} />
          ))}
        </Panel>
      ))}

      {cursor !== null && (
        <div style={{ justifySelf: 'center' }}>
          <Button variant="secondary" disabled={loading} onClick={() => void load(cursor)}>
            {loading ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      )}
    </Page>
  );
}

export function IncidentDetail({ id }: { id: string }) {
  const t = useT();
  const [incident, setIncident] = useState<IncidentWire | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { navigate } = useRouter();

  useEffect(() => {
    api
      .incident(id)
      .then((r) => setIncident(r.incident))
      .catch((err) => setError(errorText(err, 'Could not load that incident.')));
  }, [id]);

  if (error) {
    return (
      <Page>
        <Panel style={{ display: 'grid', gap: 10 }}>
          <h1 style={display(24)}>{t('incidents.notfound')}</h1>
          <Footnote>{error}</Footnote>
          <span>
            <Link to="/incidents" style={{ ...monoPlain, textDecoration: 'none' }}>
              {t('incidents.back_all')}
            </Link>
          </span>
        </Panel>
      </Page>
    );
  }

  return (
    <Page>
      <button
        type="button"
        onClick={() => navigate('/incidents')}
        style={{
          ...monoPlain,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          justifySelf: 'start'
        }}
      >
        {t('incidents.back_all')}
      </button>
      <Panel>
        {incident ? (
          <IncidentCard incident={incident} linkTitle={false} />
        ) : (
          <Footnote>{t('incidents.loading')}</Footnote>
        )}
      </Panel>
    </Page>
  );
}
